"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { z, type TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search as SearchIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/button";

const formSchema = z.object({
  q: z.string(),
});

type FormData = TypeOf<typeof formSchema>;

export function Search({ className }: { className?: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { register, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      q: searchParams.get("q") || "",
    },
  });

  const onSubmit = (values: FormData) => {
    if (values.q.length > 0) {
      router.push(`/blog/search?q=${encodeURIComponent(values.q)}`);
    } else {
      router.push(`/blog/page/1`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={cn("my-5 p-1 overflow-hidden", className)}>
        <div className="md:flex items-center space-y-2 md:space-x-2 md:space-y-0">
          <div className="relative rounded-md basis-2/3">
            <input
              {...register("q")}
              className="block w-full rounded-md border-0 py-2.5 pr-10 text-gray-950 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600"
              type="text"
              placeholder="Enter keywords..."
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <SearchIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
            </div>
          </div>
          <div className="flex items-center basis-1/3 space-x-3">
            <Button className="w-full ml-auto" type="submit" variant="ghost">
              Search
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

export function SearchSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("my-5 p-1 overflow-hidden", className)}>
      <div className="md:flex items-center space-y-2 md:space-x-2 md:space-y-0">
        <div className="relative rounded-md basis-2/3">
          <div className="block w-full rounded-md border-0 py-2.5 pr-10 bg-gray-200 animate-pulse h-11" />
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="w-4 h-4 bg-white animate-pulse" />
          </div>
        </div>
        <div className="flex items-center basis-1/3 space-x-3">
          <div className="w-full ml-auto h-11 bg-blue-600 rounded-md animate-pulse" />
        </div>
      </div>
    </div>
  );
}
